var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { observable, makeObservable, action } from "mobx";
import i18n from "@/services/store/i18n";
import detectBrowserLanguage from "detect-browser-language";
import { PaymentPlatform } from "@/api/partners/partners.types";
import MPSessionStorage from "@/services/MPSessionStorage";
import { normalizeAppLanguage } from "@/utilities/i18n/normalizeLanguage";
import { applyMixpanelUserFromProfile } from "@/utilities/mixpanel/Mixpanel";
var removeQuotes = function (str) { return str.replace(/['"]+/g, ""); };
var AdminState = /** @class */ (function () {
    function AdminState() {
        var _this = this;
        this.currentUser = null;
        this.paymentInfo = null;
        this.paymentMethodData = null;
        this.pbsPaymentInfo = null;
        this.currentPartner = null;
        this.channels = null;
        this.globalWarning = false;
        this.currentClient = null;
        this.pbsBundleId = undefined;
        this.paymentMethods = null;
        this.paymentMethodsPolling = false;
        this.balanceSettings = {};
        this.channelsFilterOptions = new Map();
        this.acceptedTos = null;
        this.bearerTokenAuthDisabled = false;
        this.newChannel = {};
        this.onboardingInfo = {};
        this.billingData = {};
        this.phoneNumberId = null;
        this.wabaId = null;
        this.isWaLoginUser = false;
        this.partnerBundles = [];
        this.paymentPlanList = [];
        this.reset = function () {
            _this.currentUser = null;
            _this.paymentInfo = null;
            _this.paymentMethodData = null;
            _this.pbsPaymentInfo = null;
            _this.channels = null;
            _this.currentPartner = null;
            _this.currentClient = null;
            _this.paymentMethods = null;
            _this.pbsBundleId = undefined;
            _this.bearerTokenAuthDisabled = false;
            _this.channelsFilterOptions = new Map();
        };
        this.destroyLoggedUser = function () {
            _this.currentUser = null;
            _this.currentClient = null;
            _this.currentPartner = null;
            if (_this.paymentInfo || _this.pbsPaymentInfo) {
                _this.paymentInfo = null;
                _this.pbsPaymentInfo = null;
            }
        };
        this.setCurrentClient = function (client) {
            _this.currentClient = client;
            MPSessionStorage.save(MPSessionStorage.keys.CURRENT_CLIENT, client);
        };
        this.insertUserData = function (user) {
            _this.currentUser = user;
            MPSessionStorage.save(MPSessionStorage.keys.CURRENT_USER, user);
            void applyMixpanelUserFromProfile(user);
        };
        this.insertUserMetaData = function (user) {
            if (_this.currentUser) {
                if (user.name !== undefined) {
                    _this.currentUser.name = user.name;
                }
                if (user.picture !== undefined) {
                    _this.currentUser.picture = user.picture;
                }
            }
        };
        this.setOnboardingInfo = function (info) {
            _this.onboardingInfo = __assign(__assign({}, _this.onboardingInfo), info);
        };
        this.setPartnerBundles = function (bundles) {
            _this.partnerBundles = bundles;
        };
        this.setChannels = function (channels) {
            _this.channels = channels;
        };
        this.setChannel = function (channel) {
            _this.newChannel = channel;
        };
        this.removeChannels = function () {
            _this.channels = null;
        };
        this.setFilterOptionsForPartner = function (partnerId, options) {
            _this.channelsFilterOptions.set(partnerId, options);
        };
        this.setPaymentMethods = function (paymentMethods) {
            // Create a safe copy to prevent MobX observable array issues
            _this.paymentMethods = paymentMethods ? __spreadArray([], __read(paymentMethods), false) : null;
        };
        this.showWarning = function (val) {
            _this.globalWarning = val;
        };
        this.setLanguage = function (language) {
            _this.currentLanguage = language;
            i18n.changeLanguage(language);
        };
        var query = new URLSearchParams(location.search);
        var qLang = query.get("lang");
        if (qLang) {
            this.setLanguage(normalizeAppLanguage(qLang));
        }
        else if (i18n.language) {
            this.setLanguage(normalizeAppLanguage(removeQuotes(i18n.language)));
        }
        else {
            this.setLanguage(normalizeAppLanguage(detectBrowserLanguage));
        }
        makeObservable(this, {
            currentLanguage: observable,
            currentUser: observable,
            paymentInfo: observable,
            paymentMethodData: observable,
            currentPartner: observable,
            insertUserData: action,
            channels: observable,
            removeChannels: observable,
            globalWarning: observable,
            showWarning: action,
            channelsFilterOptions: observable,
            acceptedTos: observable,
            bearerTokenAuthDisabled: observable,
            billingData: observable,
            onboardingInfo: observable,
            newChannel: observable,
            balanceSettings: observable,
            pbsBundleId: observable,
            paymentMethods: observable,
            paymentMethodsPolling: observable,
            partnerBundles: observable,
            paymentPlanList: observable
        });
    }
    AdminState.prototype.setBearerTokenAuthDisabled = function (value) {
        var _a;
        if ((_a = this.currentPartner) === null || _a === void 0 ? void 0 : _a.settings) {
            this.currentPartner.settings.bearerTokenAuthDisabled = value;
        }
    };
    Object.defineProperty(AdminState.prototype, "permissions", {
        get: function () {
            if (this.currentUser) {
                return this.currentUser.permissions;
            }
            else {
                throw new Error("Can not get user permissions");
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AdminState.prototype, "getCurrentUser", {
        get: function () {
            if (this.currentUser) {
                return this.currentUser;
            }
            else {
                throw new Error("Can not get user");
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AdminState.prototype, "currentClientData", {
        get: function () {
            if (this.currentClient) {
                return this.currentClient;
            }
            else {
                throw new Error("Can not get client");
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AdminState.prototype, "hasEbanxPaymentProcessor", {
        get: function () {
            var _a, _b;
            return ((_b = (_a = this.paymentMethods) === null || _a === void 0 ? void 0 : _a.some(function (method) { return method.platform === PaymentPlatform.EBANX; })) !== null && _b !== void 0 ? _b : false);
        },
        enumerable: false,
        configurable: true
    });
    return AdminState;
}());
export default AdminState;
//# sourceMappingURL=data:application/json;base64,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